import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Mail, Phone, MapPin } from 'lucide-react';

interface Customer {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city?: string | null;
  postcode: string | null;
  payment_status: string;
  created_at: string;
  updated_at: string;
  payment_links: {
    id: string;
    amount: number;
    currency: string;
    description: string | null;
    review_link_included: boolean | null;
  } | null;
}

interface CustomerCardProps {
  customer: Customer;
  onStatusUpdate: (customerId: string, newStatus: string) => void;
  onSendReminder: (customer: Customer) => void;
  onViewStoredPdf: (customer: Customer) => Promise<void>;
  onEmailReceipt: (customer: Customer) => void;
  onDeleteCustomer: (customer: Customer) => void;
  onEditCustomer: (customer: Customer) => void;
  onSendDocument?: (customer: Customer, kind: 'reminder' | 'invoice' | 'receipt' | 'estimate' | 'review') => void;
  onConvertEstimate?: (customer: Customer) => void;
  sendingEmail: boolean;
}

const CustomerCard = ({
  customer,
  onStatusUpdate,
  onViewStoredPdf,
  onDeleteCustomer,
  onEditCustomer,
  onSendDocument,
  onConvertEstimate,
  sendingEmail,
}: CustomerCardProps) => {
  const [openingPdf, setOpeningPdf] = React.useState(false);

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);

  const isPaid = customer.payment_status === 'paid';
  const isEstimate = customer.payment_status === 'estimate';

  // Status pill styling — current stage is emphasised (stronger bg, bolder border, subtle ring)
  const statusPill =
    isPaid
      ? 'bg-green-100 border-green-400 text-green-800 ring-2 ring-green-200'
      : isEstimate
      ? 'bg-blue-100 border-blue-400 text-blue-800 ring-2 ring-blue-200'
      : customer.payment_status === 'overdue'
      ? 'bg-red-100 border-red-500 text-red-800 ring-2 ring-red-300 shadow-sm'
      : 'bg-yellow-100 border-yellow-400 text-yellow-800 ring-2 ring-yellow-200';

  // Dynamic "View PDF" label based on stage
  const viewLabel = isPaid ? 'View Receipt' : isEstimate ? 'View Estimate' : 'View Invoice';

  const addressString = [customer.address, customer.city, customer.postcode].filter(Boolean).join(', ');
  const mapsHref = addressString
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`
    : null;

  return (
    <Card className="border-l-4 border-l-mint-300 hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-5">
        {/* Header: name + amount */}
        <div className="flex justify-between items-start gap-3 mb-4">
          <h3 className="text-lg font-bold text-slate-900 leading-tight">
            {customer.name || 'Unknown Customer'}
          </h3>
          {customer.payment_links && (
            <span className="text-xl font-bold text-slate-900 whitespace-nowrap">
              {formatCurrency(customer.payment_links.amount, customer.payment_links.currency)}
            </span>
          )}
        </div>

        {/* Status / action pills — only show "Mark as Paid" action while unpaid (not estimate) */}
        <div className="flex gap-2 mb-5">
          {!isPaid && !isEstimate && (
            <button
              onClick={() => onStatusUpdate(customer.id, 'paid')}
              className="flex-1 h-9 rounded-full bg-green-100 border border-green-300 text-green-800 text-[10px] sm:text-[11px] md:text-xs font-semibold uppercase tracking-wide hover:bg-green-200 transition-colors"
            >
              Mark as Paid
            </button>
          )}

          <div
            className={`flex-1 h-9 rounded-full border text-[10px] sm:text-[11px] md:text-xs font-semibold uppercase tracking-wide flex items-center justify-center ${statusPill}`}
          >
            {customer.payment_status}
          </div>

          <button
            onClick={async () => {
              try {
                setOpeningPdf(true);
                await onViewStoredPdf(customer);
              } finally {
                setOpeningPdf(false);
              }
            }}
            disabled={openingPdf}
            className="flex-1 h-9 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] sm:text-[11px] md:text-xs font-semibold uppercase tracking-wide hover:bg-blue-100 transition-colors inline-flex items-center justify-center disabled:opacity-60"
          >
            {openingPdf ? 'Opening...' : viewLabel}
          </button>
        </div>


        {/* Contact info — clickable */}
        <div className="space-y-3 mb-5">
          {customer.email && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-slate-500" />
              </div>
              <a
                href={`mailto:${customer.email}`}
                className="text-sm text-slate-700 font-medium underline decoration-slate-200 underline-offset-4 hover:decoration-mint-400 break-all"
              >
                {customer.email}
              </a>
            </div>
          )}

          {customer.phone && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-slate-500" />
              </div>
              <a
                href={`tel:${customer.phone.replace(/\s+/g, '')}`}
                className="text-sm text-slate-700 font-medium underline decoration-slate-200 underline-offset-4 hover:decoration-mint-400"
              >
                {customer.phone}
              </a>
            </div>
          )}

          {addressString && mapsHref && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 mt-0.5">
                <MapPin className="w-4 h-4 text-slate-500" />
              </div>
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-700 font-medium underline decoration-slate-200 underline-offset-4 hover:decoration-mint-400 leading-relaxed"
              >
                {addressString}
              </a>
            </div>
          )}
        </div>

        {customer.payment_links?.review_link_included && !isEstimate && (
          <div className="mb-5 p-2.5 rounded-lg bg-mint-50 border border-mint-100 text-xs font-medium text-mint-700">
            ✓ Review link included in original request
          </div>
        )}

        {/* Secondary action grid */}
        {isEstimate ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onConvertEstimate?.(customer)}
            className="w-full h-9 text-[10px] sm:text-[11px] md:text-xs font-medium"
          >
            Convert to Payment Request
          </Button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditCustomer(customer)}
              className="h-9 text-[10px] sm:text-[11px] md:text-xs"
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDeleteCustomer(customer)}
              className="h-9 text-[10px] sm:text-[11px] md:text-xs"
            >
              Delete
            </Button>

            <div className="col-span-2 flex items-center gap-2 py-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                Send to Customer
              </span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onSendDocument?.(customer, 'reminder')}
              disabled={sendingEmail || isPaid || (!customer.email && !customer.phone)}
              title={isPaid ? "Customer is already paid" : !customer.email && !customer.phone ? "Add an email or phone number first" : undefined}
              className="h-9 text-[10px] sm:text-[11px] md:text-xs"
            >
              Payment Reminder
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSendDocument?.(customer, 'invoice')}
              disabled={!customer.email && !customer.phone}
              title={!customer.email && !customer.phone ? "Add an email or phone number first" : undefined}
              className="h-9 text-[10px] sm:text-[11px] md:text-xs"
            >
              Invoice Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSendDocument?.(customer, 'receipt')}
              disabled={!customer.email && !customer.phone}
              title={!customer.email && !customer.phone ? "Add an email or phone number first" : undefined}
              className="h-9 text-[10px] sm:text-[11px] md:text-xs"
            >
              Receipt Copy
            </Button>
            {customer.payment_links?.review_link_included && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSendDocument?.(customer, 'review')}
                disabled={sendingEmail || (!customer.email && !customer.phone)}
                title={!customer.email && !customer.phone ? "Add an email or phone number first" : undefined}
                className="h-9 text-[10px] sm:text-[11px] md:text-xs"
              >
                Review Link
              </Button>
            )}

          </div>
        )}
      </div>
    </Card>
  );
};

export default CustomerCard;
